import {
  PayloadAction,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { Paging } from "types/common.types";

import { SelectOptions } from "components/Common/Select";

import { RootState } from "store/store";

import {
  createMeetingTask,
  createTask,
  deleteTask,
  getAllTasks,
  getMyTask,
  getTask,
  getTaskPriorities,
  getTaskTypes,
  getUserTaskAssignment,
  getUserTasks,
  updateMyTask,
  updateTask,
} from "./actions";
import { Task } from "./types";

export type TaskState = {
  loading: boolean;
  error: any;
  taskTypes: SelectOptions[];
  taskPriorities: SelectOptions[];
  meta: Paging;
  counts: Record<string, number | undefined>;
  initialTaskUserId: number | null;
};

export const taskAdapter = createEntityAdapter<Task>();

export const tasksSlice = createSlice({
  name: "tasks",
  initialState: taskAdapter.getInitialState<TaskState>({
    loading: false,
    error: false,
    taskTypes: [],
    taskPriorities: [],
    meta: {} as Paging,
    counts: {},
    initialTaskUserId: null,
  }),
  reducers: {
    tasksSetAll: taskAdapter.setAll,
    taskAdd: taskAdapter.addOne,
    taskRemove: taskAdapter.removeOne,
    taskUpdate: taskAdapter.updateOne,
    taskAddOne: taskAdapter.addOne,
    setInitialTaskUserId: (state, action: PayloadAction<number | null>) => {
      state.initialTaskUserId = action.payload;
    },
  },
  extraReducers: (builder) => {
    //create task
    builder.addCase(createTask.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(createTask.pending, (state) => {
      state.loading = false;
      state.error = false;
    });
    builder.addCase(createTask.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      taskAdapter.addOne(state, action.payload.data);
    });

    //create meeting task
    builder.addCase(createMeetingTask.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(createMeetingTask.pending, (state) => {
      state.loading = false;
      state.error = false;
    });
    builder.addCase(createMeetingTask.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      taskAdapter.addOne(state, action.payload.data);
    });

    //task types
    builder.addCase(getTaskTypes.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getTaskTypes.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.taskTypes = action.payload.data;
    });
    builder.addCase(getTaskTypes.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    //task priorities
    builder.addCase(getTaskPriorities.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getTaskPriorities.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.taskPriorities = action.payload.data;
    });
    builder.addCase(getTaskPriorities.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    //get user tasks
    builder.addCase(getUserTasks.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getUserTasks.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getUserTasks.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.meta = action.payload.meta;
      state.counts = action.payload.counts;
      taskAdapter.setAll(state, action.payload.data);
    });

    //get all tasks
    builder.addCase(getAllTasks.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getAllTasks.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getAllTasks.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.meta = action.payload.meta;
      state.counts = action.payload.counts;
      taskAdapter.setAll(state, action.payload.data);
    });

    //get my task
    builder.addCase(getMyTask.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getMyTask.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getMyTask.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      taskAdapter.setOne(state, action.payload.data);
    });

    //get global task
    builder.addCase(getTask.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getTask.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getTask.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      taskAdapter.setOne(state, action.payload.data);
    });

    //get user tasks assignment
    builder.addCase(getUserTaskAssignment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getUserTaskAssignment.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getUserTaskAssignment.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      taskAdapter.setOne(state, action.payload.data);
    });

    //update task
    builder.addCase(updateTask.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(updateTask.pending, (state) => {
      state.loading = false;
      state.error = false;
    });
    builder.addCase(updateTask.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      taskAdapter.upsertOne(state, action.payload.data);
    });

    //update my task
    builder.addCase(updateMyTask.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(updateMyTask.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(updateMyTask.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      taskAdapter.upsertOne(state, action.payload.data);
    });

    //delete task
    builder.addCase(deleteTask.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(deleteTask.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(deleteTask.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      taskAdapter.removeOne(state, action.meta.arg);
    });
  },
});

// selectors

export const { selectAll: selectAllTasks, selectById: selectTaskById } =
  taskAdapter.getSelectors<RootState>((state) => state.tasks);
export const tasksLoading = (state: RootState) => state.tasks.loading;

// Action creators are generated for each case reducer function
export const { setInitialTaskUserId } = tasksSlice.actions;
export default tasksSlice.reducer;
